import React, { useState, useEffect, useCallback, useRef } from 'react';
import MicroApiChannel from './MicroApiChannel';

/**
 * MicroApiHost - Rendered inside spawned windows.
 *
 * Establishes a communication channel with the parent SDK window and
 * renders content based on metadata.type. This is the integration layer
 * between Silo's window system and the SDK's spawned content.
 *
 * Props (from DraggableWindow rendering):
 * - hostApi: Silo's host API
 * - windowId: This window's ID
 * - metadata: { parentWindowId, type, config, isSpawned, ... }
 */
function MicroApiHost({ hostApi, windowId, metadata }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const channelRef = useRef(null);

  useEffect(() => {
    const parentWindowId = metadata?.parentWindowId;
    if (!parentWindowId) {
      setError('No parent window ID provided');
      return;
    }

    // Create communication channel with deterministic ID
    const channelId = `${parentWindowId}:${windowId}`;
    const channel = new MicroApiChannel(channelId);
    channelRef.current = channel;

    // Listen for all messages from parent SDK
    const unsubscribe = channel.on('*', (payload, type) => {
      if (type === 'command') {
        handleCommand(payload);
      } else {
        setMessages(prev => [...prev.slice(-50), { type, payload, ts: Date.now() }]);
      }
    });

    // Notify parent that this window is ready
    channel.send('lifecycle', { status: 'ready', windowId });
    setReady(true);

    return () => {
      channel.send('lifecycle', { status: 'closing', windowId });
      unsubscribe();
      channel.destroy();
      channelRef.current = null;
    };
  }, [windowId, metadata?.parentWindowId]);

  const handleCommand = useCallback((payload) => {
    // Handle commands from parent SDK (extensible)
    switch (payload?.action) {
      case 'clear':
        setMessages([]);
        break;
      default:
        setMessages(prev => [...prev.slice(-50), { type: 'command', payload, ts: Date.now() }]);
    }
  }, []);

  const sendToParent = useCallback((type, payload) => {
    if (channelRef.current) {
      channelRef.current.send(type, payload);
    }
  }, []);

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <span style={styles.errorIcon}>!</span>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Initializing...</div>
      </div>
    );
  }

  // Render content based on metadata.type
  switch (metadata?.type) {
    case 'status':
      return <StatusView messages={messages} metadata={metadata} sendToParent={sendToParent} />;
    case 'data-view':
      return <DataView messages={messages} metadata={metadata} sendToParent={sendToParent} />;
    default:
      return <DefaultView messages={messages} metadata={metadata} windowId={windowId} sendToParent={sendToParent} />;
  }
}

/**
 * DefaultView - Shows channel status and recent messages.
 */
function DefaultView({ messages, metadata, windowId, sendToParent }) {
  const dataMessages = messages.filter(m => m.type === 'data');
  const latestData = dataMessages.length > 0 ? dataMessages[dataMessages.length - 1] : null;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.title}>Micro API</span>
        <span style={styles.badge}>Connected</span>
      </div>
      <div style={styles.info}>
        <div style={styles.infoRow}>
          <span style={styles.label}>Window:</span>
          <span style={styles.value}>{windowId}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.label}>Parent:</span>
          <span style={styles.value}>{metadata?.parentWindowId}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.label}>Type:</span>
          <span style={styles.value}>{metadata?.type || 'default'}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.label}>Messages:</span>
          <span style={styles.value}>{messages.length}</span>
        </div>
      </div>
      {latestData && (
        <div style={styles.dataSection}>
          <div style={styles.sectionTitle}>Latest Data</div>
          <pre style={styles.pre}>{JSON.stringify(latestData.payload, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

/**
 * StatusView - Displays status data from parent.
 */
function StatusView({ messages, metadata, sendToParent }) {
  const dataMessages = messages.filter(m => m.type === 'data');
  const latestData = dataMessages.length > 0 ? dataMessages[dataMessages.length - 1] : null;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.title}>Status</span>
        {latestData && <span style={styles.badge}>Live</span>}
      </div>
      <div style={styles.content}>
        {latestData ? (
          <pre style={styles.pre}>{JSON.stringify(latestData.payload, null, 2)}</pre>
        ) : (
          <div style={styles.waiting}>Waiting for data...</div>
        )}
      </div>
    </div>
  );
}

/**
 * DataView - Renders structured data from parent.
 */
function DataView({ messages, metadata, sendToParent }) {
  const dataMessages = messages.filter(m => m.type === 'data');

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.title}>Data View</span>
        <span style={styles.badge}>{dataMessages.length} items</span>
      </div>
      <div style={styles.scrollContent}>
        {dataMessages.length === 0 ? (
          <div style={styles.waiting}>Waiting for data...</div>
        ) : (
          dataMessages.map((msg, i) => (
            <div key={i} style={styles.dataItem}>
              <pre style={styles.pre}>{JSON.stringify(msg.payload, null, 2)}</pre>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: '#1a1a2e',
    color: '#e0e0e0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace',
    fontSize: '13px',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderBottom: '1px solid #2a2a4a',
    flexShrink: 0
  },
  title: {
    fontWeight: 600,
    fontSize: '14px'
  },
  badge: {
    background: '#2d5a3d',
    color: '#6ee7b7',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px'
  },
  info: {
    padding: '10px 14px',
    borderBottom: '1px solid #2a2a4a',
    flexShrink: 0
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 0'
  },
  label: {
    color: '#888'
  },
  value: {
    color: '#a5b4fc',
    fontFamily: 'monospace',
    fontSize: '12px',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  content: {
    flex: 1,
    padding: '14px',
    overflow: 'auto'
  },
  scrollContent: {
    flex: 1,
    padding: '10px 14px',
    overflow: 'auto'
  },
  dataSection: {
    flex: 1,
    padding: '10px 14px',
    overflow: 'auto'
  },
  sectionTitle: {
    fontSize: '11px',
    color: '#888',
    textTransform: 'uppercase',
    marginBottom: '6px'
  },
  pre: {
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: '12px',
    lineHeight: '1.5',
    color: '#d4d4d4'
  },
  dataItem: {
    padding: '8px',
    background: '#16162a',
    borderRadius: '4px',
    marginBottom: '6px'
  },
  waiting: {
    color: '#666',
    fontStyle: 'italic',
    padding: '20px 0',
    textAlign: 'center'
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#888'
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px',
    color: '#f87171'
  },
  errorIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: '#7f1d1d',
    fontSize: '12px',
    fontWeight: 'bold'
  }
};

export default MicroApiHost;
